import React, { useContext, useState } from 'react';
import { Box, Typography, Chip, Divider, LinearProgress, Button } from '@mui/material';
import MountIcon from '@mui/icons-material/Explore';
import CameraIcon from '@mui/icons-material/CameraAlt';
import FilterIcon from '@mui/icons-material/FilterAlt';
import FocuserIcon from '@mui/icons-material/Adjust';
import RotatorIcon from '@mui/icons-material/RotateRight';
import DomeIcon from '@mui/icons-material/Home';
import WeatherIcon from '@mui/icons-material/Cloud';
import GuiderIcon from '@mui/icons-material/CenterFocusWeak';
import SafetyMonitorIcon from '@mui/icons-material/Security';
import SwitchIcon from '@mui/icons-material/PowerSettingsNew';
import FlatPanelIcon from '@mui/icons-material/WbSunny';
import ToggleIcon from '@mui/icons-material/ToggleOn';
import { ObservatoryContext } from '../../contexts/ObservatoryContext';
import EquipmentStatusCard, { decimalToSexagesimal, formatTrackingMode } from '../common/EquipmentStatusCard';

// Props `observatory` and `telemetry` override the context, so this
// component can render from a plain list entry (with embedded
// equipment_status) without needing ObservatoryProvider above it.
const EquipmentDetailCards = ({ observatory: observatoryProp, telemetry: telemetryProp, compact = false }) => {
  const ctx = useContext(ObservatoryContext);
  const telemetry = telemetryProp !== undefined ? telemetryProp : ctx?.telemetry;
  const observatory = observatoryProp !== undefined ? observatoryProp : ctx?.observatory;
  // Per-device "Last Reported Status" toggle state, keyed by device.
  const [lastReportedShown, setLastReportedShown] = useState({});

  // When the observatory is offline/inactive the live telemetry is stale (and a
  // late WebSocket frame can re-populate it), so blank it out — every device
  // reads as disconnected. The last-connected snapshot still drives each card's
  // "Last Reported Status" control.
  const offline = observatory?.status === 'offline' || observatory?.status === 'inactive';
  const eqBase = offline ? {} : (telemetry?.equipment_status || {});

  // Per-device snapshot of the last values seen while connected. The REST list
  // payload carries it on `observatory`; live telemetry may carry it too.
  const lastConnected = telemetry?.last_connected || observatory?.last_connected || {};
  const LAST_REPORTED_DEVICES = ['mount', 'camera', 'filterWheel', 'focuser', 'rotator', 'dome', 'weather', 'safetyMonitor', 'switches', 'flatDevice'];

  // Build the per-card view: for a disconnected device whose toggle is on, swap
  // in the snapshot (it carries connected:true, so the normal connected layout
  // renders its values) and mark it stale so the card is tinted.
  const staleDevices = {};
  const eq = { ...eqBase };
  for (const k of LAST_REPORTED_DEVICES) {
    if (!eqBase[k]?.connected && lastReportedShown[k] && lastConnected[k]) {
      eq[k] = lastConnected[k];
      staleDevices[k] = true;
    }
  }

  const formatLastSeen = (at) => {
    if (!at) return null;
    try {
      return new Date(at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return null; }
  };
  // Toggle button (+ "as of" stamp) for a disconnected device that has a
  // snapshot. Returns null when the device is connected or has no snapshot.
  const renderLastReportedControl = (key) => {
    if (eqBase[key]?.connected) return null;
    if (!lastConnected[key]) return null;
    const showing = !!lastReportedShown[key];
    return (
      <Box sx={{ mt: 0.5 }}>
        <Button
          size="small"
          variant={showing ? 'outlined' : 'text'}
          color="warning"
          onClick={() => setLastReportedShown((s) => ({ ...s, [key]: !s[key] }))}
          sx={{ fontSize: '0.65rem', py: 0, minWidth: 0, textTransform: 'none', lineHeight: 1.3 }}
        >
          {showing ? 'Hide last reported' : 'Last Reported Status'}
        </Button>
        {showing && lastConnected[key]?.at && (
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.6rem', display: 'block', fontStyle: 'italic' }}>
            as of {formatLastSeen(lastConnected[key].at)}
          </Typography>
        )}
      </Box>
    );
  };

  const ninaOptions = observatory?.software?.nina?.options;
  const telescopeConfig = observatory?.configuration?.telescope_config || {};

  // In compact mode, cards shrink to a flex-basis of ~320px and gain a
  // visible outlined border so boundaries read clearly when two-plus
  // cards share a row in a narrow window. Elevation mode keeps the
  // original 512px width for full-size pages.
  const cardBoxSx = compact
    ? { flex: '1 1 320px', minWidth: 280, maxWidth: '100%' }
    : { width: 512, maxWidth: '100%' };

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: compact ? 1 : 2 }}>
      {/* Mount — always show */}
      <Box sx={cardBoxSx}>
        <EquipmentStatusCard compact={compact} title="Mount" icon={<MountIcon color="primary" />}
          connected={eq.mount?.connected} data={eq.mount} equipmentName={eq.mount?.name}
          stale={staleDevices.mount} lastReportedControl={renderLastReportedControl('mount')}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Status:</Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                {eq.mount?.state ? (
                  <>
                    <Chip label={eq.mount.state} size="small"
                      color={eq.mount.state === 'TRACKING' ? 'primary' : eq.mount.state === 'GUIDING' ? 'success' : eq.mount.state === 'PARKED' ? 'default' : eq.mount.state === 'SLEWING' ? 'warning' : 'default'} />
                    {eq.mount.state_detail && <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>({eq.mount.state_detail})</Typography>}
                  </>
                ) : (
                  <>
                    {eq.mount?.parked && <Chip label="Parked" size="small" />}
                    {eq.mount?.tracking && <Chip label={`Tracking (${formatTrackingMode(eq.mount?.tracking_mode, eq.mount?.custom_ra_rate, eq.mount?.custom_dec_rate)})`} size="small" color="primary" />}
                    {eq.mount?.slewing && <Chip label="Slewing" size="small" color="warning" />}
                    {!eq.mount?.parked && !eq.mount?.tracking && !eq.mount?.slewing && <Chip label="Idle" size="small" />}
                  </>
                )}
              </Box>
            </Box>
            <Row label="RA:" value={decimalToSexagesimal(eq.mount?.ra, true)} mono />
            <Row label="Dec:" value={decimalToSexagesimal(eq.mount?.dec, false)} mono />
            <Row label="Alt/Az:" value={`${eq.mount?.altitude?.toFixed(1) || '—'}° / ${eq.mount?.azimuth?.toFixed(1) || '—'}°`} />
            {eq.mount?.pier_side && eq.mount.pier_side !== 'pierUnknown' && eq.mount.pier_side !== 'None' && (
              <Row label="Pier Side:" value={eq.mount.pier_side} />
            )}
            {/* Telescope Details — from NINA options or observatory config */}
            {(() => {
              const focalLength = eq.mount?.focal_length_mm || ninaOptions?.equipment?.telescope?.focal_length_mm || telescopeConfig.focal_length_mm;
              const focalRatio = eq.mount?.focal_ratio || ninaOptions?.equipment?.telescope?.focal_ratio || telescopeConfig.focal_ratio;
              const settleTime = ninaOptions?.equipment?.mount?.settle_time_seconds;
              if (!focalLength && !focalRatio && !settleTime) return null;
              return (
                <>
                  <Divider sx={{ my: 0.5 }} />
                  <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.7rem', color: 'text.secondary', mb: 0.5 }}>Telescope Details</Typography>
                  {focalLength > 0 && <Row label="Focal Length:" value={`${focalLength}mm`} mono />}
                  {focalRatio > 0 && <Row label="Focal Ratio:" value={`f/${focalRatio}`} mono />}
                  {settleTime != null && <Row label="Settle Time:" value={`${settleTime}s`} />}
                  {ninaOptions?.equipment?.mount?.no_sync && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2">Sync:</Typography>
                      <Chip label="Disabled" size="small" color="warning" />
                    </Box>
                  )}
                </>
              );
            })()}
            {eq.mount?.driver_version && (<><Divider sx={{ my: 0.5 }} /><Row label="Driver:" value={eq.mount.driver_version} mono /></>)}
          </Box>
        </EquipmentStatusCard>
      </Box>

      {/* Camera — always show */}
      <Box sx={cardBoxSx}>
        <EquipmentStatusCard compact={compact} title="Camera" icon={<CameraIcon color="primary" />}
          connected={eq.camera?.connected} data={eq.camera} equipmentName={eq.camera?.name}
          stale={staleDevices.camera} lastReportedControl={renderLastReportedControl('camera')}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {eq.camera?.temperature != null && (
              <Row label="Temperature:" value={`${Number(eq.camera.temperature).toFixed(2)}°C`} />
            )}
            {eq.camera?.target_temperature != null && (
              <Row label="Target Temp:" value={`${Number(eq.camera.target_temperature).toFixed(2)}°C`} />
            )}
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Cooler:</Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Chip label={eq.camera?.cooler_on ? 'ON' : 'OFF'} size="small" color={eq.camera?.cooler_on ? 'success' : 'default'} />
                {eq.camera?.cooler_power != null && <Typography variant="body2">{Math.round(eq.camera.cooler_power)}%</Typography>}
              </Box>
            </Box>
            <Row label="Binning:" value={`${eq.camera?.bin_x || '?'}x${eq.camera?.bin_y || '?'}`} />
            {(eq.camera?.gain !== undefined || eq.camera?.offset !== undefined) && (
              <Row label="Gain/Offset:" value={`${eq.camera?.gain ?? 'N/A'}/${eq.camera?.offset ?? 'N/A'}`} />
            )}
            {eq.camera?.camera_state && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">State:</Typography>
                <Chip label={eq.camera.camera_state} size="small" color={eq.camera?.is_exposing ? 'primary' : 'default'} />
              </Box>
            )}
            {eq.camera?.is_exposing && eq.camera?.exposure_elapsed !== undefined && (
              <>
                <Row label={eq.camera?.exposure_index ? `Exposure ${eq.camera.exposure_index}/${eq.camera.exposure_count || '?'}:` : 'Exposure:'}
                  value={eq.camera?.exposure_total ? `${eq.camera.exposure_elapsed.toFixed(0)}s / ${eq.camera.exposure_total}s` : `${eq.camera.exposure_elapsed.toFixed(1)}s`} />
                {eq.camera?.exposure_progress !== undefined && (
                  <Box sx={{ mt: 0.5 }}>
                    <LinearProgress variant="determinate" value={eq.camera.exposure_progress} sx={{ height: 6, borderRadius: 1 }} />
                  </Box>
                )}
              </>
            )}
            {eq.camera?.pixel_size != null && (<><Divider sx={{ my: 0.5 }} /><Row label="Pixel Size:" value={`${Number(eq.camera.pixel_size).toFixed(2)} μm`} /></>)}
            {eq.camera?.sensor_width && eq.camera?.sensor_height && <Row label="Resolution:" value={`${eq.camera.sensor_width} × ${eq.camera.sensor_height}`} />}
            {eq.camera?.driver_version && (<><Divider sx={{ my: 0.5 }} /><Row label="Driver:" value={eq.camera.driver_version} mono /></>)}
          </Box>
        </EquipmentStatusCard>
      </Box>

      {/* Filter Wheel — when connected, or disconnected with a snapshot */}
      {(eqBase.filterWheel?.connected || lastConnected.filterWheel) && (
        <Box sx={cardBoxSx}>
          <EquipmentStatusCard compact={compact} title="Filter Wheel" icon={<FilterIcon color="primary" />}
            connected={eq.filterWheel?.connected} data={eq.filterWheel} equipmentName={eq.filterWheel?.name}
            stale={staleDevices.filterWheel} lastReportedControl={renderLastReportedControl('filterWheel')}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Current Filter:</Typography>
                <Chip label={eq.filterWheel?.current_filter || 'Unknown'} size="small" color="secondary" />
              </Box>
              <Row label="Position:" value={eq.filterWheel?.position} />
              {eq.filterWheel?.filter_names?.length > 0 && (
                <Box sx={{ mt: 0.5 }}>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>Available Filters:</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {eq.filterWheel.filter_names.map((f, i) => <Chip key={i} label={f} size="small" variant="outlined" />)}
                  </Box>
                </Box>
              )}
              {eq.filterWheel?.driver_version && (<><Divider sx={{ my: 0.5 }} /><Row label="Driver:" value={eq.filterWheel.driver_version} mono /></>)}
            </Box>
          </EquipmentStatusCard>
        </Box>
      )}

      {/* Focuser — when connected, or disconnected with a snapshot */}
      {(eqBase.focuser?.connected || lastConnected.focuser) && (
        <Box sx={cardBoxSx}>
          <EquipmentStatusCard compact={compact} title="Focuser" icon={<FocuserIcon color="primary" />}
            connected={eq.focuser?.connected} data={eq.focuser} equipmentName={eq.focuser?.name}
            stale={staleDevices.focuser} lastReportedControl={renderLastReportedControl('focuser')}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Row label="Position:" value={eq.focuser?.position} />
              <Row label="Temperature:" value={eq.focuser?.temperature != null ? `${eq.focuser.temperature}°C` : 'N/A'} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Moving:</Typography>
                <Chip label={eq.focuser?.is_moving ? 'Yes' : 'No'} size="small" color={eq.focuser?.is_moving ? 'warning' : 'default'} />
              </Box>
              {eq.focuser?.driver_version && (<><Divider sx={{ my: 0.5 }} /><Row label="Driver:" value={eq.focuser.driver_version} mono /></>)}
            </Box>
          </EquipmentStatusCard>
        </Box>
      )}

      {/* Guider — only when connected */}
      {eq.guider?.connected && (
        <Box sx={cardBoxSx}>
          <EquipmentStatusCard compact={compact} title="Guider" icon={<GuiderIcon color="primary" />}
            connected={eq.guider?.connected} data={eq.guider} equipmentName={eq.guider?.name}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Guiding:</Typography>
                <Chip label={eq.guider?.guiding ? 'Active' : 'Inactive'} size="small" color={eq.guider?.guiding ? 'success' : 'default'} />
              </Box>
              <Row label="RMS Total:" value={typeof eq.guider?.rms_total === 'number' ? `${eq.guider.rms_total.toFixed(2)}"` : (typeof eq.guider?.rms_error === 'number' ? `${eq.guider.rms_error.toFixed(2)}"` : '—')} />
              <Row label="RMS RA/Dec:" value={`${typeof eq.guider?.rms_ra === 'number' ? eq.guider.rms_ra.toFixed(2) : typeof eq.guider?.ra_rms === 'number' ? eq.guider.ra_rms.toFixed(2) : '—'}" / ${typeof eq.guider?.rms_dec === 'number' ? eq.guider.rms_dec.toFixed(2) : typeof eq.guider?.dec_rms === 'number' ? eq.guider.dec_rms.toFixed(2) : '—'}"`} />
              {eq.guider?.driver_version && (<><Divider sx={{ my: 0.5 }} /><Row label="Driver:" value={eq.guider.driver_version} mono /></>)}
            </Box>
          </EquipmentStatusCard>
        </Box>
      )}

      {/* Rotator — when connected, or disconnected with a snapshot */}
      {(eqBase.rotator?.connected || lastConnected.rotator) && (
        <Box sx={cardBoxSx}>
          <EquipmentStatusCard compact={compact} title="Rotator" icon={<RotatorIcon color="primary" />}
            connected={eq.rotator?.connected} data={eq.rotator} equipmentName={eq.rotator?.name}
            stale={staleDevices.rotator} lastReportedControl={renderLastReportedControl('rotator')}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Row label="Position:" value={typeof eq.rotator?.position === 'number' ? `${eq.rotator.position.toFixed(1)}°` : '—'} />
              <Row label="Mechanical:" value={typeof eq.rotator?.mechanical_position === 'number' ? `${eq.rotator.mechanical_position.toFixed(1)}°` : '—'} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Moving:</Typography>
                <Chip label={eq.rotator?.is_moving ? 'Yes' : 'No'} size="small" color={eq.rotator?.is_moving ? 'warning' : 'default'} />
              </Box>
              {eq.rotator?.driver_version && (<><Divider sx={{ my: 0.5 }} /><Row label="Driver:" value={eq.rotator.driver_version} mono /></>)}
            </Box>
          </EquipmentStatusCard>
        </Box>
      )}

      {/* Dome — when connected, or disconnected with a snapshot */}
      {(eqBase.dome?.connected || lastConnected.dome) && (
        <Box sx={cardBoxSx}>
          <EquipmentStatusCard compact={compact} title="Dome" icon={<DomeIcon color="primary" />}
            connected={eq.dome?.connected} data={eq.dome} equipmentName={eq.dome?.name}
            stale={staleDevices.dome} lastReportedControl={renderLastReportedControl('dome')}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Row label="Shutter:" value={eq.dome?.shutter_status || 'Unknown'} />
              {typeof eq.dome?.azimuth === 'number' && <Row label="Azimuth:" value={`${eq.dome.azimuth.toFixed(1)}°`} />}
              {(eq.dome?.at_park || eq.dome?.slewing) && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Status:</Typography>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {eq.dome?.at_park && <Chip label="Parked" size="small" />}
                    {eq.dome?.slewing && <Chip label="Slewing" size="small" color="warning" />}
                  </Box>
                </Box>
              )}
              {eq.dome?.driver_version && (<><Divider sx={{ my: 0.5 }} /><Row label="Driver:" value={eq.dome.driver_version} mono /></>)}
            </Box>
          </EquipmentStatusCard>
        </Box>
      )}

      {/* Weather — when connected, or disconnected with a snapshot */}
      {(eqBase.weather?.connected || lastConnected.weather) && (
        <Box sx={cardBoxSx}>
          <EquipmentStatusCard compact={compact} title="Weather Station" icon={<WeatherIcon color="primary" />}
            connected={eq.weather?.connected} data={eq.weather} equipmentName={eq.weather?.name}
            stale={staleDevices.weather} lastReportedControl={renderLastReportedControl('weather')}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {eq.weather?.temperature != null && <Row label="Temperature:" value={`${Number(eq.weather.temperature).toFixed(1)}°C / ${(Number(eq.weather.temperature) * 9/5 + 32).toFixed(1)}°F`} mono />}
              {eq.weather?.humidity != null && <Row label="Humidity:" value={`${Number(eq.weather.humidity).toFixed(0)}%`} />}
              {eq.weather?.dewpoint != null && <Row label="Dew Point:" value={`${Number(eq.weather.dewpoint).toFixed(1)}°C`} mono />}
              {eq.weather?.wind_speed != null && <Row label="Wind Speed:" value={`${Number(eq.weather.wind_speed).toFixed(1)} m/s / ${(Number(eq.weather.wind_speed) * 2.237).toFixed(1)} mph`} mono />}
              {eq.weather?.pressure != null && <Row label="Pressure:" value={`${Number(eq.weather.pressure).toFixed(0)} hPa`} mono />}
              {eq.weather?.cloud_cover != null && <Row label="Cloud Cover:" value={`${Number(eq.weather.cloud_cover).toFixed(0)}%`} />}
              {eq.weather?.sky_quality != null && <Row label="Sky Quality:" value={`${Number(eq.weather.sky_quality).toFixed(2)} mag/arcsec²`} />}
              {eq.weather?.driver_version && (<><Divider sx={{ my: 0.5 }} /><Row label="Driver:" value={eq.weather.driver_version} mono /></>)}
            </Box>
          </EquipmentStatusCard>
        </Box>
      )}

      {/* Switch Hub — when connected, or disconnected with a snapshot */}
      {(eqBase.switches?.connected || lastConnected.switches) && (
        <Box sx={cardBoxSx}>
          <EquipmentStatusCard compact={compact} title="Switch Hub" icon={<SwitchIcon color="primary" />}
            connected={eq.switches?.connected} data={eq.switches} equipmentName={eq.switches?.name}
            stale={staleDevices.switches} lastReportedControl={renderLastReportedControl('switches')}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {eq.switches?.writable_switches?.length > 0 && (
                <>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>Controls</Typography>
                  {eq.switches.writable_switches.map(sw => (
                    <Box key={sw.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2">{sw.name || `Switch ${sw.id}`}</Typography>
                      {sw.minimum === 0 && sw.maximum === 1 ? (
                        <Chip icon={<ToggleIcon />} label={sw.value === 1 ? 'ON' : 'OFF'} size="small" color={sw.value === 1 ? 'success' : 'default'} />
                      ) : (
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{sw.value?.toFixed(1)}</Typography>
                      )}
                    </Box>
                  ))}
                </>
              )}
              {eq.switches?.readonly_switches?.length > 0 && (
                <>
                  {eq.switches?.writable_switches?.length > 0 && <Divider sx={{ my: 0.5 }} />}
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>Sensors</Typography>
                  {eq.switches.readonly_switches.map(sw => (
                    <Box key={sw.id} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2">{sw.name || `Sensor ${sw.id}`}</Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{sw.value != null ? sw.value.toFixed(1) : 'N/A'}</Typography>
                    </Box>
                  ))}
                </>
              )}
              {eq.switches?.driver_version && (<><Divider sx={{ my: 0.5 }} /><Row label="Driver:" value={eq.switches.driver_version} mono /></>)}
            </Box>
          </EquipmentStatusCard>
        </Box>
      )}

      {/* Flat Device — when connected, or disconnected with a snapshot */}
      {(eqBase.flatDevice?.connected || lastConnected.flatDevice) && (
        <Box sx={cardBoxSx}>
          <EquipmentStatusCard compact={compact} title="Flat Device" icon={<FlatPanelIcon color="primary" />}
            connected={eq.flatDevice?.connected} data={eq.flatDevice} equipmentName={eq.flatDevice?.name}
            stale={staleDevices.flatDevice} lastReportedControl={renderLastReportedControl('flatDevice')}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Light:</Typography>
                <Chip label={eq.flatDevice?.light_on ? 'ON' : 'OFF'} size="small" color={eq.flatDevice?.light_on ? 'warning' : 'default'} />
              </Box>
              {eq.flatDevice?.brightness !== undefined && (
                <Row label="Brightness:" value={`${eq.flatDevice.brightness}%`} />
              )}
              {eq.flatDevice?.cover_state && eq.flatDevice.cover_state !== 'NotPresent' && (
                <Row label="Cover:" value={eq.flatDevice.cover_state} />
              )}
              {eq.flatDevice?.driver_version && (<><Divider sx={{ my: 0.5 }} /><Row label="Driver:" value={eq.flatDevice.driver_version} mono /></>)}
            </Box>
          </EquipmentStatusCard>
        </Box>
      )}

      {/* Safety Monitor — always show, always last */}
      <Box sx={cardBoxSx}>
        <EquipmentStatusCard compact={compact} title="Safety Monitor" icon={<SafetyMonitorIcon color="primary" />}
          connected={eq.safetyMonitor?.connected} data={eq.safetyMonitor} equipmentName={eq.safetyMonitor?.name}
          stale={staleDevices.safetyMonitor} lastReportedControl={renderLastReportedControl('safetyMonitor')}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Status:</Typography>
              <Chip label={eq.safetyMonitor?.is_safe ? 'SAFE' : 'UNSAFE'} size="small" color={eq.safetyMonitor?.is_safe ? 'success' : 'error'} />
            </Box>
            {eq.safetyMonitor?.driver_version && (<><Divider sx={{ my: 0.5 }} /><Row label="Driver:" value={eq.safetyMonitor.driver_version} mono /></>)}
          </Box>
        </EquipmentStatusCard>
      </Box>
    </Box>
  );
};

// Simple label-value row helper
const Row = ({ label, value, mono }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
    <Typography variant="body2">{label}</Typography>
    <Typography variant="body2" sx={mono ? { fontFamily: 'monospace' } : undefined}>{value}</Typography>
  </Box>
);

export default EquipmentDetailCards;
